import { useState, useEffect } from "react";
import axios from "axios";
import {
  TrendingUp,
  TrendingDown,
  Award,
  Target,
  RefreshCw,
  Info
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Benchmarks = () => {
  const [benchmarks, setBenchmarks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchBenchmarks();
  }, []);

  const fetchBenchmarks = async () => {
    setRefreshing(true);
    try {
      const response = await axios.get(`${API}/benchmarks?days=30`);
      setBenchmarks(response.data);
    } catch (error) {
      console.error("Error fetching benchmarks:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8 space-y-6" data-testid="benchmarks-loading">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[300px]" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  const chartData = [
    {
      name: "Your Portfolio",
      return: benchmarks?.portfolio_return || 0,
      fill: "#3B82F6",
    },
    {
      name: "S&P 500",
      return: benchmarks?.sp500_return || 0,
      fill: benchmarks?.outperforming_sp500 ? "#10B981" : "#EF4444",
    },
    {
      name: "FTSE 100",
      return: benchmarks?.ftse100_return || 0,
      fill: "#737373",
    },
    {
      name: "Hedge Fund Avg",
      return: benchmarks?.hedge_fund_avg || 0,
      fill: benchmarks?.outperforming_hedge_funds ? "#10B981" : "#EF4444",
    },
  ];

  const targetROI = 20;
  const currentROI = benchmarks?.portfolio_return || 0;
  const progressToTarget = Math.min(100, Math.max(0, (currentROI / targetROI) * 100));

  // Top Hedge Funds for comparison (simulated)
  const hedgeFunds = [
    { name: "Bridgewater Associates", return: 2.8, aum: "$150B" },
    { name: "Renaissance Technologies", return: 4.2, aum: "$130B" },
    { name: "Two Sigma", return: 3.1, aum: "$60B" },
    { name: "Citadel", return: 2.5, aum: "$52B" },
    { name: "D.E. Shaw", return: 2.9, aum: "$55B" },
  ];

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-slide-up" data-testid="benchmarks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Performance Benchmarks
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Compare your portfolio against market indices and top hedge funds
          </p>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={fetchBenchmarks}
          disabled={refreshing}
          data-testid="refresh-benchmarks-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Main Comparison Chart */}
      <Card data-testid="comparison-chart-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm uppercase tracking-widest font-medium" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              30-Day Return Comparison
            </CardTitle>
            <Badge variant="outline">{benchmarks?.period_days || 30} days</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical" margin={{ left: 100, right: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis 
                  type="number" 
                  stroke="#737373" 
                  fontSize={10}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => `${value.toFixed(1)}%`}
                />
                <YAxis 
                  type="category" 
                  dataKey="name" 
                  stroke="#737373" 
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  width={100}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#0A0A0A',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}
                  formatter={(value) => [`${value.toFixed(2)}%`, 'Return']}
                />
                <Bar dataKey="return" radius={[0, 4, 4, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Performance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Portfolio Return */}
        <Card className="card-hover gradient-glow" data-testid="portfolio-return-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">
                Your Return
              </p>
              <Target className="w-5 h-5 text-primary" />
            </div>
            <p className={`text-3xl font-bold ${benchmarks?.portfolio_return >= 0 ? 'text-success' : 'text-destructive'}`} style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {benchmarks?.portfolio_return?.toFixed(2) || 0}%
            </p>
            <div className="mt-3">
              <p className="text-xs text-muted-foreground mb-1">Progress to 20% Target</p>
              <Progress value={progressToTarget} className="h-1.5" />
            </div>
          </CardContent>
        </Card>

        {/* vs S&P 500 */}
        <Card className="card-hover" data-testid="vs-sp500-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">
                vs S&P 500
              </p>
              {benchmarks?.outperforming_sp500 ? (
                <TrendingUp className="w-5 h-5 text-success" />
              ) : (
                <TrendingDown className="w-5 h-5 text-destructive" />
              )}
            </div>
            <p className="text-3xl font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {benchmarks?.sp500_return?.toFixed(2) || 0}%
            </p>
            <Badge 
              className={`mt-2 ${benchmarks?.outperforming_sp500 ? 'bg-success/20 text-success' : 'bg-destructive/20 text-destructive'}`}
            >
              {benchmarks?.outperforming_sp500 ? 'Outperforming' : 'Underperforming'}
            </Badge>
          </CardContent>
        </Card>

        {/* vs FTSE 100 */}
        <Card className="card-hover" data-testid="vs-ftse-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">
                vs FTSE 100
              </p>
              <Badge variant="outline" className="text-[10px]">UK</Badge>
            </div>
            <p className="text-3xl font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {benchmarks?.ftse100_return?.toFixed(2) || 0}%
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              UK market benchmark
            </p>
          </CardContent>
        </Card>

        {/* vs Hedge Funds */}
        <Card className="card-hover" data-testid="vs-hedge-funds-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">
                vs Hedge Funds
              </p>
              {benchmarks?.outperforming_hedge_funds ? (
                <Award className="w-5 h-5 text-success" />
              ) : (
                <TrendingDown className="w-5 h-5 text-destructive" />
              )}
            </div>
            <p className="text-3xl font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {benchmarks?.hedge_fund_avg?.toFixed(2) || 0}%
            </p>
            <Badge 
              className={`mt-2 ${benchmarks?.outperforming_hedge_funds ? 'bg-success/20 text-success' : 'bg-destructive/20 text-destructive'}`}
            >
              {benchmarks?.outperforming_hedge_funds ? 'Beating Avg' : 'Below Avg'}
            </Badge>
          </CardContent>
        </Card>
      </div>

      {/* Hedge Fund Comparison Table */}
      <Card data-testid="hedge-fund-table-card">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Award className="w-5 h-5 text-primary" />
            <CardTitle className="text-sm uppercase tracking-widest font-medium" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Top 10 Hedge Fund Comparison
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Fund Name</th>
                  <th>30D Return</th>
                  <th>AUM</th>
                  <th>Your Performance</th>
                </tr>
              </thead>
              <tbody>
                {hedgeFunds.map((fund, index) => {
                  const outperforming = (benchmarks?.portfolio_return || 0) > fund.return;
                  return (
                    <tr key={index} data-testid={`hedge-fund-row-${index}`}>
                      <td className="font-medium">#{index + 1}</td>
                      <td>{fund.name}</td>
                      <td className="tabular-nums">{fund.return.toFixed(2)}%</td>
                      <td className="text-muted-foreground">{fund.aum}</td>
                      <td>
                        <Badge className={outperforming ? 'bg-success/20 text-success' : 'bg-destructive/20 text-destructive'}>
                          {outperforming ? (
                            <><TrendingUp className="w-3 h-3 mr-1" /> Beating</>
                          ) : (
                            <><TrendingDown className="w-3 h-3 mr-1" /> Behind</>
                          )}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex items-start gap-2 mt-4 p-3 bg-muted/30 rounded-md">
            <Info className="w-4 h-4 text-muted-foreground mt-0.5" />
            <p className="text-xs text-muted-foreground">
              Hedge fund returns are simulated estimates based on industry averages. 
              Actual fund performance data is proprietary and may vary significantly.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Analysis */}
      <Card data-testid="analysis-card">
        <CardHeader>
          <CardTitle className="text-sm uppercase tracking-widest font-medium" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Performance Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {benchmarks?.outperforming_sp500 && benchmarks?.outperforming_hedge_funds ? (
              <div className="flex items-start gap-3 p-4 bg-success/10 border border-success/20 rounded-md">
                <Award className="w-6 h-6 text-success" />
                <div>
                  <h4 className="font-medium text-success">Excellent Performance</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Your portfolio is outperforming both the S&P 500 and average hedge fund returns. 
                    Keep monitoring and consider taking some profits to lock in gains.
                  </p>
                </div>
              </div>
            ) : benchmarks?.outperforming_sp500 || benchmarks?.outperforming_hedge_funds ? (
              <div className="flex items-start gap-3 p-4 bg-warning/10 border border-warning/20 rounded-md">
                <TrendingUp className="w-6 h-6 text-warning" />
                <div>
                  <h4 className="font-medium text-warning">Good Performance</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Your portfolio is performing well against some benchmarks. 
                    Review AI suggestions to optimize your allocation strategy.
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3 p-4 bg-muted border border-border rounded-md">
                <Info className="w-6 h-6 text-muted-foreground" />
                <div>
                  <h4 className="font-medium">Room for Improvement</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Your portfolio is currently underperforming benchmarks. 
                    Consider reviewing the AI suggestions and rebalancing your holdings.
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Disclaimer */}
      <div className="text-center py-4 border-t border-border">
        <p className="text-xs text-muted-foreground">
          Performance data is for informational purposes only. Past performance does not guarantee future results.
        </p>
      </div>
    </div>
  );
};

export default Benchmarks;
